import PropTypes from 'prop-types';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { Container } from 'semantic-ui-react';
import TeaserImages from '../Helpers/TeaserImages';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import { normalizePerson, vocabTitle } from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';

const CourseView = (props) => {
  const { content } = props;
  const academicLevel = content.course_academic_level;
  const courseNumber = content.course_number;
  const courseEcts = content.course_ects;
  const courseEffort = content.course_effort;
  const courseSemester = content.course_semester;
  const courseYear = content.course_year;
  const courseStartDate = content.course_start_date;
  const courseEndDate = content.course_end_date;
  const courseDates = content.course_dates;
  const courseDescription = content.course_description;
  const courseRequirements = content.course_requirements;
  const courseBasisLink = content.course_basis_link;
  const courseEcampusLink = content.course_ecampus_link;

  // Teaser images (only from teaser_image fields, not lead image)
  const hasImages = content.teaser_image1 || content.teaser_image2;

  // Short metadata: left column (general) and right column (time/place)
  const hasMetaLeft = courseNumber || courseEcts || courseEffort;
  const hasMetaRight =
    courseSemester?.length > 0 ||
    courseYear?.length > 0 ||
    courseStartDate ||
    courseEndDate ||
    courseDates;

  // Links
  const hasLinks = courseBasisLink || courseEcampusLink;

  const hideLecturers = content.hide_related_lecturers;
  const hideContacts = content.hide_related_contacts;
  const hideTutors = content.hide_related_tutors;

  const lecturers = content.related_lecturers
    ?.map(normalizePerson)
    .filter(Boolean);
  const contacts = content.related_contacts
    ?.map(normalizePerson)
    .filter(Boolean);
  const tutors = content.related_tutors?.map(normalizePerson).filter(Boolean);

  const showLecturer = !hideLecturers && lecturers?.length > 0;
  const showContact = !hideContacts && contacts?.length > 0;
  const showTutor = !hideTutors && tutors?.length > 0;
  const showProjects =
    !content.hide_related_projects && content.related_projects?.length > 0;
  const showPublications =
    !content.hide_related_publications &&
    content.related_publications?.length > 0;
  const showTopics =
    !content.hide_related_topics && content.related_topics?.length > 0;
  const hasRelations =
    showLecturer ||
    showContact ||
    showTutor ||
    showProjects ||
    showPublications ||
    showTopics;

  // Helper: normalize academic_level (may be array, object, or string)
  const academicLevelLabel = (() => {
    const val = academicLevel;
    if (!val) return null;
    if (Array.isArray(val))
      return val.map((v) => (v && v.title ? v.title : v)).join(', ');
    return val.title || val;
  })();

  const hasRequirements = (() => {
    if (!courseRequirements) return false;
    if (typeof courseRequirements === 'string')
      return courseRequirements.trim() !== '';
    return courseRequirements?.data && courseRequirements.data !== '<p></p>';
  })();

  const hasMeta = hasMetaLeft || hasMetaRight;

  return (
    <Container id="view-wrapper" className="CourseView">
      {/* Header */}
      <div className="view-header">
        {academicLevelLabel && (
          <p className="view-header__subtitle">{academicLevelLabel}</p>
        )}
        <h2 className="view-header__title">
          {content.type_of_course
            ? `${vocabTitle(content.type_of_course)} ${content.title || content.id}`
            : content.title || content.id}
        </h2>
        {courseDescription?.data && courseDescription.data !== '<p></p>' && (
          <div
            className="view-header__description"
            dangerouslySetInnerHTML={{
              __html: flattenRichTextToAppURL(courseDescription.data),
            }}
          />
        )}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* Images and metadata read as one section. */}
        {(hasImages || hasMeta) && (
          <>
            {/* Teaser Images */}
            {hasImages && <TeaserImages content={content} />}

            {/* 2-Column Metadata */}
            {hasMeta && (
              <div className="view-meta-grid">
                {/* Left: General */}
                <div className="view-meta-grid__col">
                  {courseNumber && (
                    <div className="view-section">
                      <h3 className="view-section__label">Course Number:</h3>
                      <p className="view-section__value">{courseNumber}</p>
                    </div>
                  )}
                  {courseEcts && (
                    <div className="view-section">
                      <h3 className="view-section__label">ECTS:</h3>
                      <p className="view-section__value">{courseEcts}</p>
                    </div>
                  )}
                  {courseEffort && (
                    <div className="view-section">
                      <h3 className="view-section__label">Effort:</h3>
                      <p className="view-section__value">{courseEffort}</p>
                    </div>
                  )}
                </div>

                {/* Right: Time / Place */}
                <div className="view-meta-grid__col">
                  {courseSemester?.length > 0 && (
                    <div className="view-section">
                      <h3 className="view-section__label">Semester:</h3>
                      <p className="view-section__value">
                        {courseSemester.join(', ')}
                      </p>
                    </div>
                  )}
                  {courseYear?.length > 0 && (
                    <div className="view-section">
                      <h3 className="view-section__label">Year:</h3>
                      <p className="view-section__value">
                        {courseYear.join(', ')}
                      </p>
                    </div>
                  )}
                  {courseStartDate && (
                    <div className="view-section">
                      <h3 className="view-section__label">Start:</h3>
                      <p className="view-section__value">{courseStartDate}</p>
                    </div>
                  )}
                  {courseEndDate && (
                    <div className="view-section">
                      <h3 className="view-section__label">End:</h3>
                      <p className="view-section__value">{courseEndDate}</p>
                    </div>
                  )}
                  {courseDates && (
                    <div className="view-section">
                      <h3 className="view-section__label">Dates:</h3>
                      <p className="view-section__value">{courseDates}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Requirements (RichText) */}
        {hasRequirements && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Requirements:</h3>
              {typeof courseRequirements === 'string' ? (
                <p className="view-section__value">{courseRequirements}</p>
              ) : (
                <div
                  className="view-section__richtext"
                  dangerouslySetInnerHTML={{
                    __html: flattenRichTextToAppURL(courseRequirements.data),
                  }}
                />
              )}
            </div>
          </>
        )}

        {/* Links */}
        {hasLinks && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Links:</h3>
              <div className="view-section__buttons">
                {courseBasisLink && (
                  <a
                    href={courseBasisLink}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Basis
                  </a>
                )}
                {courseEcampusLink && (
                  <a
                    href={courseEcampusLink}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    E-Campus
                  </a>
                )}
              </div>
            </div>
          </>
        )}

        {/* Relations */}
        {hasRelations && (
          <>
            {showLecturer && (
              <div className="view-section">
                <h3 className="view-section__label">Lecturers:</h3>
                <RelatedItemsDisplay
                  items={lecturers}
                  mode={content.related_lecturers_display}
                  kind="person"
                  fieldKey={`${content['@id']}-related_lecturers`}
                />
              </div>
            )}
            {showContact && (
              <div className="view-section">
                <h3 className="view-section__label">Contact:</h3>
                <RelatedItemsDisplay
                  items={contacts}
                  mode={content.related_contacts_display}
                  kind="person"
                  fieldKey={`${content['@id']}-related_contacts`}
                />
              </div>
            )}
            {showTutor && (
              <div className="view-section">
                <h3 className="view-section__label">Tutors:</h3>
                <RelatedItemsDisplay
                  items={tutors}
                  mode={content.related_tutors_display}
                  kind="person"
                  fieldKey={`${content['@id']}-related_tutors`}
                />
              </div>
            )}
            {showProjects && (
              <div className="view-section">
                <h3 className="view-section__label">Related Projects:</h3>
                <RelatedItemsDisplay
                  items={content.related_projects}
                  mode={content.related_projects_display}
                  kind="plain"
                  fieldKey={`${content['@id']}-related_projects`}
                />
              </div>
            )}
            {showPublications && (
              <div className="view-section">
                <h3 className="view-section__label">Related Publications:</h3>
                <RelatedItemsDisplay
                  items={content.related_publications}
                  mode={content.related_publications_display}
                  kind="plain"
                  fieldKey={`${content['@id']}-related_publications`}
                />
              </div>
            )}
            {showTopics && (
              <div className="view-section">
                <h3 className="view-section__label">Related Topics:</h3>
                <RelatedItemsDisplay
                  items={content.related_topics}
                  mode={content.related_topics_display}
                  kind="plain"
                  fieldKey={`${content['@id']}-related_topics`}
                />
              </div>
            )}
          </>
        )}
      </Sections>

      {/* The block-rendered part of the page */}
      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

CourseView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    type_of_course: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    teaser_image1: PropTypes.object,
    teaser_image1_caption: PropTypes.string,
    teaser_image1_copyright: PropTypes.string,
    teaser_image2: PropTypes.object,
    teaser_image2_caption: PropTypes.string,
    teaser_image2_copyright: PropTypes.string,
    hide_related_lecturers: PropTypes.bool,
    hide_related_contacts: PropTypes.bool,
    hide_related_tutors: PropTypes.bool,
    related_lecturers_display: PropTypes.string,
    related_contacts_display: PropTypes.string,
    related_tutors_display: PropTypes.string,
    related_projects_display: PropTypes.string,
    related_publications_display: PropTypes.string,
    related_topics_display: PropTypes.string,
    related_lecturers: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        is_linkable: PropTypes.bool,
        full_name: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    related_contacts: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        is_linkable: PropTypes.bool,
        full_name: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    related_tutors: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        is_linkable: PropTypes.bool,
        full_name: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    hide_related_projects: PropTypes.bool,
    related_projects: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
    hide_related_publications: PropTypes.bool,
    related_publications: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
    hide_related_topics: PropTypes.bool,
    related_topics: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
  }).isRequired,
};

export default CourseView;
